/**
 * ProductosApiClient -- HTTP client para productos canon v1.2.
 *
 * Mision: PRODUCTOS-SYNC-POSTGRES-V1 / T6.
 *
 * Endpoint productivo NexoAPI v1.6.26+ (Argy-A PR #58 mergeado):
 *   GET    /api/v1/productos?businessId=X[&seccionId&grupoId&q&activo&page&pageSize]
 *   GET    /api/v1/productos/{id}
 *   POST   /api/v1/productos
 *   PATCH  /api/v1/productos/{id}
 *   PATCH  /api/v1/productos/{id}/seccion
 *   POST   /api/v1/productos/{id}/categorias
 *   PATCH  /api/v1/productos/{id}/grupo
 *   DELETE /api/v1/productos/{id} (soft-delete)
 *
 * DTO canonico (camelCase desde API, lectura defensiva por si el shape evoluciona):
 *   { id, businessId, nombre, precio, costo, stock, codigo, codigoDesktop, activo,
 *     seccionId, seccionNombre, grupoId, grupoNombre,
 *     categorias: [{ id, nombre }],
 *     inventarioDirecto, rowIdDesktop, createdAt, updatedAt }
 *
 * Adapter: `listar()` aplana cada item a la shape consumida por CarritoView
 * (`{id, nombre, precio, stock, grupo, costo, canonId, seccionNombre, categorias}`).
 * Las vistas que necesitan los campos canon adicionales los reciben tambien.
 *
 * @module infrastructure/api/ProductosApiClient
 */

import { ApiBase } from './ApiBase.js';
import { llevaStock } from '../../application/carrito/stockDelProducto.js';
import { idLocalDelEscritorio } from '../../domain/value-objects/idDelEscritorio.js';

const BASE = '/api/v1/productos';

// Tope que acepta ProductosService.ListAsync antes de resetear a 25 EN SILENCIO
// (`if (pageSize < 1 || pageSize > 200) pageSize = 25;`). Nunca pedir mas que esto.
const PRODUCTOS_MAX_PAGE_SIZE = 200;

function unwrap(res) {
  return res?.data?.data ?? res?.data ?? null;
}

/**
 * Adapter REST canon -> shape comun consumida por CarritoView. Defensive con
 * varios fallbacks por si el DTO C# evoluciona o usa snake_case mixto.
 */
function adaptCanonItem(p) {
  if (!p || typeof p !== 'object') return null;
  return {
    // ID de uso: preferir codigo (legible para el operador) y caer al uuid canon
    id: p.codigo ?? p.codigoDesktop ?? p.code ?? p.rowIdDesktop ?? String(p.id ?? ''),
    nombre: p.nombre ?? p.descripcion ?? '(sin nombre)',
    precio: Number(p.precio ?? p.precioVenta ?? p.precio_venta ?? 0),
    stock: Number(p.stock ?? p.inventario ?? p.cantidad ?? 0),
    // hallazgo 8: receta (inventario directo = 0) no lleva stock propio; sin la clave, como antes (lleva).
    llevaStock: llevaStock(p),
    grupo: p.grupoNombre ?? p.grupo_nombre ?? p.grupo ?? '',
    costo: Number(p.costo ?? p.costoUnitario ?? 0),
    // T169 (#35355, D #35362, OK Ares #36322): el id LOCAL del escritorio, como numero, es lo que viaja en
    // producto_id del pedido. `id` puede ser el codigo del producto (3 de 169 lo tienen distinto del id local).
    idDesktop: idLocalDelEscritorio(p.rowIdDesktop ?? p.row_id_desktop),
    // Canon extras disponibles para vistas que los necesiten
    canonId: p.id ?? null,
    activo: p.activo !== false,
    seccionId: p.seccionId ?? p.seccion_id ?? null,
    seccionNombre: p.seccionNombre ?? p.seccion_nombre ?? null,
    grupoId: p.grupoId ?? p.grupo_id ?? null,
    categorias: Array.isArray(p.categorias) ? p.categorias : [],
    inventarioDirecto: p.inventarioDirecto === true || p.inventario_directo === true
  };
}

export const ProductosApiClient = {
  /**
   * Lista productos del negocio, con filtros server-side opcionales. Devuelve
   * items adaptados a shape comun + total/page/pageSize del envelope.
   * @param {{ businessId:string, seccionId?:number, grupoId?:number, q?:string,
   *           activo?:boolean, page?:number, pageSize?:number }} params
   */
  async listar({ businessId, seccionId, grupoId, q, activo, page, pageSize } = {}) {
    if (!businessId) return { ok: false, error: 'businessId requerido', code: 'BUSINESS_ID_REQUIRED' };
    const qs = new URLSearchParams();
    qs.set('businessId', businessId);
    if (seccionId != null) qs.set('seccionId', String(seccionId));
    if (grupoId != null) qs.set('grupoId', String(grupoId));
    if (q) qs.set('q', q);
    if (typeof activo === 'boolean') qs.set('activo', String(activo));
    if (page) qs.set('page', String(page));
    if (pageSize) qs.set('pageSize', String(pageSize));
    const res = await ApiBase.get(`${BASE}?${qs}`);
    if (!res.ok) return res;
    const data = unwrap(res) || {};
    const rawItems = Array.isArray(data.items) ? data.items
                   : Array.isArray(data)        ? data
                   : [];
    const items = rawItems.map(adaptCanonItem).filter(Boolean);
    return {
      ok: true,
      data: {
        items,
        total: Number(data.total ?? items.length),
        page: Number(data.page ?? 1),
        pageSize: Number(data.pageSize ?? items.length)
      }
    };
  },

  /**
   * Lista TODOS los productos del negocio paginando en loop. Usar cuando se
   * necesita el catalogo completo (ej. grid del carrito).
   *
   * CART-1 (bit #2994/#2995): el backend ProductosService.ListAsync hace
   *   `if (pageSize < 1 || pageSize > 200) pageSize = 25;`
   * => cualquier pageSize > 200 se RESETEA A 25 EN SILENCIO (sin error, sin 400).
   * Por eso el carrito de EC mostraba 25 de 96 productos pidiendo pageSize=500.
   * Aqui nunca pedimos mas de 200 y paginamos hasta agotar `total`, de modo que
   * el panel NO depende de que el backend cambie el clamp.
   *
   * @param {{ businessId:string, seccionId?:number, grupoId?:number, q?:string,
   *           activo?:boolean, pageSize?:number, maxPages?:number }} params
   * @returns {Promise<{ok:boolean, data?:{items:Array,total:number,pages:number,partial:boolean}}>}
   *          `partial:true` si se corto por maxPages o por fallo de una pagina >1
   *          (se devuelve lo acumulado en vez de perder todo el catalogo).
   */
  async listarTodos({ businessId, seccionId, grupoId, q, activo, pageSize = 200, maxPages = 25 } = {}) {
    if (!businessId) return { ok: false, error: 'businessId requerido', code: 'BUSINESS_ID_REQUIRED' };

    const size = Math.min(Math.max(Number(pageSize) || PRODUCTOS_MAX_PAGE_SIZE, 1), PRODUCTOS_MAX_PAGE_SIZE);
    const all = [];
    let page = 1;
    let total = null;
    let partial = false;

    while (page <= maxPages) {
      const res = await ProductosApiClient.listar({ businessId, seccionId, grupoId, q, activo, page, pageSize: size });
      if (!res.ok) {
        if (page === 1) return res;      // sin nada que devolver -> propagar el error
        partial = true; break;           // ya hay items -> devolver lo acumulado
      }
      const items = res.data?.items || [];
      all.push(...items);
      total = Number(res.data?.total ?? all.length);

      if (items.length === 0 || items.length < size || all.length >= total) break;
      page++;
      if (page > maxPages) partial = true;
    }

    return { ok: true, data: { items: all, total: total ?? all.length, pages: page, partial } };
  },

  /**
   * Obtiene un producto por uuid canon. Devuelve item adaptado.
   * @param {{ id:string|number }} params
   */
  async obtener({ id } = {}) {
    if (id == null) return { ok: false, error: 'id requerido', code: 'ID_REQUIRED' };
    const res = await ApiBase.get(`${BASE}/${encodeURIComponent(id)}`);
    if (!res.ok) return res;
    const data = unwrap(res) || {};
    const raw = data.item || data;
    const item = adaptCanonItem(raw);
    return { ok: true, data: { item, raw } };
  },

  /**
   * Crea un producto. Backend valida UNIQUE (business_id, row_id_desktop) -> 409.
   * @param {{ item:object }} params
   */
  async crear({ item } = {}) {
    if (!item || !item.nombre || !String(item.nombre).trim()) {
      return { ok: false, error: 'nombre requerido', code: 'VALIDATION' };
    }
    const res = await ApiBase.post(BASE, item);
    if (!res.ok) return res;
    const data = unwrap(res) || {};
    const raw = data.item || data;
    return { ok: true, data: { item: adaptCanonItem(raw), raw } };
  },

  /**
   * Actualiza un producto (PATCH partial). Backend valida all-fields-null -> 400.
   * @param {{ id:string|number, partial:object }} params
   */
  async actualizar({ id, partial } = {}) {
    if (id == null) return { ok: false, error: 'id requerido', code: 'ID_REQUIRED' };
    if (!partial || Object.keys(partial).length === 0) {
      return { ok: false, error: 'partial vacio', code: 'VALIDATION' };
    }
    const res = await ApiBase.patch(`${BASE}/${encodeURIComponent(id)}`, partial);
    if (!res.ok) return res;
    const data = unwrap(res) || {};
    const raw = data.item || data;
    return { ok: true, data: { item: adaptCanonItem(raw), raw } };
  },

  /**
   * Reasigna seccion de un producto. Backend valida rol=seccion (canon §5.1).
   * @param {{ id:string|number, seccionId:number|null }} params
   */
  async actualizarSeccion({ id, seccionId } = {}) {
    if (id == null) return { ok: false, error: 'id requerido', code: 'ID_REQUIRED' };
    const res = await ApiBase.patch(`${BASE}/${encodeURIComponent(id)}/seccion`, { seccionId });
    if (!res.ok) return res;
    return { ok: true, data: unwrap(res) || { id } };
  },

  /**
   * Reemplaza la coleccion de categorias del producto (DELETE+INSERT atomico).
   * @param {{ id:string|number, categoriaIds:Array<number> }} params
   */
  async actualizarCategorias({ id, categoriaIds } = {}) {
    if (id == null) return { ok: false, error: 'id requerido', code: 'ID_REQUIRED' };
    if (!Array.isArray(categoriaIds)) return { ok: false, error: 'categoriaIds debe ser array', code: 'VALIDATION' };
    const res = await ApiBase.post(`${BASE}/${encodeURIComponent(id)}/categorias`, { categoriaIds });
    if (!res.ok) return res;
    return { ok: true, data: unwrap(res) || { id, categoriaIds } };
  },

  /**
   * Reasigna grupo (solo para productos con inventario_directo=true).
   * Backend devuelve 400 si el producto NO tiene inventario_directo (canon §5.2).
   * @param {{ id:string|number, grupoId:number|null }} params
   */
  async actualizarGrupo({ id, grupoId } = {}) {
    if (id == null) return { ok: false, error: 'id requerido', code: 'ID_REQUIRED' };
    const res = await ApiBase.patch(`${BASE}/${encodeURIComponent(id)}/grupo`, { grupoId });
    if (!res.ok) return res;
    return { ok: true, data: unwrap(res) || { id } };
  },

  /**
   * Soft-delete: backend marca activo=false. Preserva historial canon.
   * @param {{ id:string|number }} params
   */
  async eliminar({ id } = {}) {
    if (id == null) return { ok: false, error: 'id requerido', code: 'ID_REQUIRED' };
    const res = await ApiBase.del(`${BASE}/${encodeURIComponent(id)}`);
    if (!res.ok) return res;
    return { ok: true, data: { id, softDeleted: true } };
  }
};
